import * as Icons from "@mui/icons-material";
import { Box, Button, MenuItem, Typography, useTheme } from "@mui/material";
import Menu from "@mui/material/Menu";
import React, { useContext, useState } from "react";

import { Workspace } from "@storyboard/web-client";
import Locator from "@/locator.ts";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { findWorkspaceQuery } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";

export default function WorkspaceMenuSelect() {
  const theme = useTheme();
  const workspaceMenuRef = React.useRef<HTMLDivElement>(null);
  const { currentWorkspaceId, workspaces } = useContext(WorkspaceContext);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState<boolean>(false);

  const workspaceRecords =
    useReactiveSelectQuery(
      findWorkspaceQuery,
      [{ workspaceId: currentWorkspaceId }],
      "WorkspaceDrawerHeader:findWorkspaceQuery",
    ) ?? [];

  const changeWorkspace = async (ws: Workspace) => {
    setWorkspaceMenuOpen(() => false);
    const newUrl = `/workspaces/${ws?.id}`;
    window.location.href = newUrl;
  };
  const truncatedName = (string: string, length: number, dots?: string): string =>
    `${string?.substring(0, length)}${string?.length >= length ? dots : ""}`;

  const workspaceRecord = workspaceRecords?.[0];

  return (
    <Box data-name="WorkspaceMenuSelect" ref={workspaceMenuRef} sx={{ width: "100%" }}>
      <Button
        variant="text"
        color="primary"
        aria-label={Locator.workspaceNav.dropdown.button}
        onClick={() => setWorkspaceMenuOpen((value) => !value)}
        sx={{
          textTransform: "none",
          p: 0,
          display: "flex",
          width: "100%",
          justifyContent: "start",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            padding: 1,
            border: `1px solid ${theme.palette.neutral.main}`,
          }}
        >
          <>{workspaceMenuOpen ? <Icons.KeyboardArrowUp role="img" /> : <Icons.KeyboardArrowDown role="img" />}</>
        </Box>

        <Box>
          <Typography
            title={workspaceRecord?.name}
            variant="h6"
            component="h2"
            sx={{ lineHeight: 1.2, fontWeight: "normal", textAlign: "left", fontSize: "18px" }}
          >
            {workspaceRecord?.name ? truncatedName(workspaceRecord?.name, 60, "...") : "Loading Workspace"}
          </Typography>
        </Box>
      </Button>
      {workspaces?.length > 0 && (
        <Menu
          anchorEl={workspaceMenuRef.current}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          open={workspaceMenuOpen}
          onClose={() => setWorkspaceMenuOpen(false)}
          sx={{ mt: 1 }}
        >
          {workspaces.map((workspace) => (
            <MenuItem
              disabled={workspace.id === currentWorkspaceId}
              key={workspace.id}
              sx={{ display: "flex", py: 1.5, gap: 1 }}
              onClick={() => changeWorkspace(workspace)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  height: 30,
                  width: 30,
                  background: workspace.id === currentWorkspaceId ? theme.palette.neutral.dark : "transparent",
                  border: `1px solid ${theme.palette.neutral.main}`,
                }}
              >
                {workspace?.name[0]}
              </Box>

              <Box>{workspace.name}</Box>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
}
